import { find } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectPermissions, selectUserGroup } from '../../store/authReducer';
import { UserGroupPermission } from '../../views/employees/models';
import { hasRole, RoleTypes } from './Permission';
import { hasUserPermission, PermissionKeys } from './UserPermissions';

interface Props {
  itemRef: string;
  minRole: string;
  children: React.ReactChild | React.ReactChildren;
}

export const viewPermissionRefs = {
  admin: 'system.admin',
  task: 'manager.task.view',
  site: 'manager.site.view',
  detergent: 'manager.detergent.view',
  detergent_wip: 'manager.detergent_wip.view',
  warranty: 'manager.warranty.view',
  rewash: 'manager.rewash.view',
  team: 'manager.team.view',
  expense: 'manager.expense.view',
  utilities: 'manager.utilities.view',
  expense_vendor: 'manager.expense_vendor.view',
  inventory: 'manager.inventory.view',
  feedback: 'manager.feedback.view',
  wash_count: 'manager.wash_count.view',
  wash_count_adjustments: 'manager.wash_count_adjustments.view',
  settings: 'manager.site_settings.view',
  damage: 'manager.damage_options.view',
  products: 'manager.products.view',
  manageUsers: 'manager.manage_users',
  analytics: 'manager.analytics.view',
  multi_site: 'manager.multi_site.view',
  revenue_comparison: 'manager.charts.revenue_comparison',
  window_sales: 'manager.charts.window_sales',
  wash_count_chart: 'manager.charts.wash_counts',
  claims_chart: 'manager.charts.claims',
  cost_per_car: 'manager.charts.cost_per_car',
  survey_chart: 'manager.charts.surveys',
  cashier_tracking: 'manager.charts.cashier_tracking',
  interior_filter: 'manager.wash_count.view_interior_filter',
  wash_analytics: 'manager.wash_count.wash_analytics',
  view_maintenance_page: 'manager.task.view_maintenance_page',
  view_projects_page: 'manager.task.view_projects_page',
  view_logs: 'manager.task.view_logs',
  view_upload: 'manager.expense.view_upload',
  essential_parts: 'manager.inventory.essential_parts',
  hourly_comparision_chart: 'manager.charts.hourly_wash_kpi',
  occupancy_chart: 'manager.charts.occupancy_kpi',
  rewash_chart_section: 'manager.charts.rewash_kpis_section',
  utility_chart_section: 'manager.charts.utility_kpis_section',
  todays_tasks_new: 'manager.today_task_wip.view',
  todays_tasks: 'manager.today_task.view',
  maintenance_page_new: 'manager.task.view_new_maintenance',
  projects_page_new: 'manager.task.view_new_projects',
  view_assets_page: 'manager.task.view_assets_page',
  assets: 'manager.asset.view',
};

export const addPermissionRefs = {
  assets: 'manager.asset.add',
  task: 'manager.task.add',
  site: 'manager.site.add',
  detergent: 'manager.detergent.add',
  warranty: 'manager.warranty.add',
  rewash: 'manager.rewash.add',
  team: 'manager.team.add',
  expense: 'manager.expense.add',
  expense_vendor: 'manager.expense_vendor.add',
  inventory: 'manager.inventory.add',
  feedback: 'manager.feedback.add',
  wash_count: 'manager.wash_count.add',
  wash_count_adjustments: 'manager.wash_count_adjustments.add',
  settings: 'manager.site_settings.add',
  damage: 'manager.damage_options.add',
  products: 'manager.products.add',
  analytics: 'manager.analytics.add',
};

export const editPermissionRefs = {
  assets: 'manager.asset.edit',
  task: 'manager.task.edit',
  site: 'manager.site.edit',
  detergent: 'manager.detergent.edit',
  warranty: 'manager.warranty.edit',
  rewash: 'manager.rewash.edit',
  team: 'manager.team.edit',
  expense: 'manager.expense.edit',
  expense_vendor: 'manager.expense_vendor.edit',
  inventory: 'manager.inventory.edit',
  feedback: 'manager.feedback.edit',
  wash_count: 'manager.wash_count.edit',
  wash_count_adjustments: 'manager.wash_count_adjustments.edit',
  settings: 'manager.site_settings.edit',
  damage: 'manager.damage_options.edit',
  products: 'manager.products.edit',
  analytics: 'manager.analytics.edit',
  claim_notification_timing: 'manager.claim_notification_timing.edit',
};

export const deletePermissionRefs = {
  assets: 'manager.asset.delete',
  task: 'manager.task.delete',
  site: 'manager.site.delete',
  detergent: 'manager.detergent.delete',
  warranty: 'manager.warranty.delete',
  rewash: 'manager.rewash.delete',
  team: 'manager.team.delete',
  expense: 'manager.expense.delete',
  expense_vendor: 'manager.expense_vendor.delete',
  inventory: 'manager.inventory.delete',
  feedback: 'manager.feedback.delete',
  wash_count: 'manager.wash_count.delete',
  wash_count_adjustments: 'manager.wash_count_adjustments.delete',
  settings: 'manager.site_settings.delete',
  damage: 'manager.damage_options.delete',
  products: 'manager.products.delete',
  analytics: 'manager.analytics.delete',
};

export const archivePermissionRefs = {
  assets: 'manager.asset.archive',
};

export const findPermissionRef = (
  permissions: UserGroupPermission[],
  ref: string
): UserGroupPermission | undefined => {
  return find(permissions, ['ref', ref]);
};

/**
 * The same decision `UserGroupPermissions` makes, as a boolean. Use it when the
 * gated thing isn't a subtree to wrap — a table's data-entry row, a submit
 * handler — so those stay in sync with the wrapped controls.
 */
export const useHasUserGroupPermission = (itemRef: string, minRole = 'SiteManager'): boolean => {
  const permissions = useSelector(selectPermissions);
  const userGroup = useSelector(selectUserGroup);
  const enhancedRole = findPermissionRef(userGroup?.permissions, viewPermissionRefs.admin)
    ? 'Admin'
    : userGroup?.role;

  if (hasRole(enhancedRole, RoleTypes.OperatorGroupAdmin)) {
    return hasRole(enhancedRole, minRole);
  }

  if (hasUserPermission(itemRef)) {
    if (!permissions) return hasRole(enhancedRole, minRole);
    if (itemRef === PermissionKeys.analytics) {
      return Boolean((permissions as Record<string, any>)[itemRef]?.enabled);
    }
    return Boolean((permissions as Record<string, any>)[itemRef]);
  }

  return Boolean(findPermissionRef(userGroup?.permissions, itemRef));
};

export const UserGroupPermissions = (props: Props): React.ReactElement<any> | JSX.Element => {
  const { itemRef, minRole, children } = props;
  const allowed = useHasUserGroupPermission(itemRef, minRole);

  return allowed ? <React.Fragment>{children}</React.Fragment> : <React.Fragment />;
};

UserGroupPermissions.defaultProps = {
  minRole: 'SiteManager',
};

export default UserGroupPermissions;
